import React from 'react';
import { Link } from 'react-router-dom';
import { Badge, DataTable } from '@/components/ui';
import type { DataTableColumn } from '@/components/ui';
import type { PoConsumptionEffort } from '../../../services/purchaseOrderService';
import { currency, shortDate } from '../../vendors/utils/format';

const STATUS_BADGE: Record<string, 'success' | 'secondary' | 'danger' | 'warning'> = {
  approved: 'success',
  pending: 'secondary',
  'need revisions': 'danger',
  'revisions completed': 'secondary',
  declined: 'secondary',
};

function statusLabel(status: string | null | undefined): string {
  if (!status) return '–';
  if (status === 'revisions completed') return 'Pending';
  return status.replace(/_/g, ' ').replace(/\b\w/g, (c) => c.toUpperCase());
}

export interface PoConsumptionEffortsTableProps {
  efforts: PoConsumptionEffort[];
  loading?: boolean;
}

/**
 * Line-level view of what is consuming a PO. Same join as the consumption bar (Effort.poNumber).
 * Declined/system rows stay visible so a missing figure is not mistaken for "never booked".
 */
export const PoConsumptionEffortsTable: React.FC<PoConsumptionEffortsTableProps> = ({
  efforts,
  loading = false,
}) => {
  const columns: DataTableColumn<PoConsumptionEffort>[] = [
    {
      key: 'date',
      header: 'Date',
      cell: (row) => shortDate(row.date),
    },
    {
      key: 'description',
      header: 'Description',
      mobile: 'title',
      cell: (row) => (
        <span className={row.bucket === 'excluded' ? 'tw-text-content-muted' : undefined}>
          {row.description || '–'}
        </span>
      ),
    },
    {
      key: 'type',
      header: 'Type',
      mobile: 'subtitle',
      cell: (row) => row.type || '–',
    },
    {
      key: 'task',
      header: 'Task',
      cell: (row) => (row.task ? row.task.taskNumber ?? row.task.name ?? '–' : '–'),
    },
    {
      key: 'quantity',
      header: 'Qty',
      align: 'right',
      cell: (row) => (row.quantity == null ? '–' : row.quantity),
    },
    {
      key: 'unitOfMeasure',
      header: 'Unit',
      mobile: 'hidden',
      cell: (row) => row.unitOfMeasure || '–',
    },
    {
      key: 'rate',
      header: 'Rate',
      align: 'right',
      mobile: 'hidden',
      cell: (row) => currency(row.rate),
    },
    {
      key: 'value',
      header: 'Value',
      align: 'right',
      mobile: 'meta',
      cell: (row) => (
        <span className="tw-tabular-nums tw-font-medium">{currency(row.value)}</span>
      ),
    },
    {
      key: 'status',
      header: 'Status',
      mobile: 'trailing',
      cell: (row) => {
        const status = row.formStatus ?? '';
        const bg = STATUS_BADGE[status.toLowerCase()] ?? 'secondary';
        return (
          <Link
            to={`/submissions?formId=${encodeURIComponent(row.formId)}`}
            className="tw-no-underline"
            onClick={(e) => e.stopPropagation()}
            title={row.formName ? `Open ${row.formName}` : 'Open submission'}
          >
            <Badge bg={bg} pill>
              {statusLabel(row.formStatus)}
            </Badge>
          </Link>
        );
      },
    },
  ];

  return (
    <DataTable
      columns={columns}
      data={efforts}
      rowKey={(row) => row.id}
      loading={loading}
      size="sm"
      framed
      emptyMessage={
        <div className="tw-py-2 tw-text-center">
          <i className="bi bi-inbox tw-text-xl tw-block tw-mb-2 tw-text-content-muted" />
          <p className="tw-text-content-muted tw-mb-0">No efforts booked against this purchase order.</p>
        </div>
      }
    />
  );
};

export default PoConsumptionEffortsTable;
